import { useMemo, useState } from 'react';
import { api, Api } from '../lib/api';
import { navigate } from '../lib/router';
import { Logo } from '../lib/ui';
import { TopbarUser, useSession } from '../App';
import { fmtPrice, useCatalog, type Catalog, type DecorRow } from '../lib/catalog';

type Tab = 'decors' | 'boards' | 'edges' | 'suppliers' | 'profiles' | 'import';
const TABS: [Tab, string][] = [
  ['decors', 'Dekori'],
  ['boards', 'Ploče'],
  ['edges', 'Kant trake'],
  ['suppliers', 'Dobavljači i cijene'],
  ['profiles', 'Profili radnji'],
  ['import', 'Uvoz cjenovnika'],
];

const mm = (um: number | null | undefined) => (um === null || um === undefined ? '—' : String(um / 1000));

export function CatalogPage() {
  const { me } = useSession();
  const canEdit = me.workspace.role !== 'viewer';
  const { catalog, error, reload } = useCatalog();
  const [tab, setTab] = useState<Tab>('decors');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setMsg(null);
    try {
      await fn();
      if (ok) setMsg({ ok: true, text: ok });
      reload();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  };
  return (
    <div>
      <header className="topbar">
        <button className="btn ghost sm" onClick={() => navigate('/')}>
          <Logo size={18} /> ←
        </button>
        <strong>Katalog materijala</strong>
        <span className="muted">{me.workspace.name}</span>
        <div className="spacer" />
        <TopbarUser />
      </header>
      <main className="dash">
        <div className="tabs" role="tablist" style={{ marginBottom: 14, flexWrap: 'wrap', display: 'inline-flex' }}>
          {TABS.map(([k, label]) => (
            <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>
        {error && <p className="error-text">{error}</p>}
        {msg && <p className={msg.ok ? 'ok-text' : 'error-text'}>{msg.text}</p>}
        {tab === 'decors' && <Decors cat={catalog} canEdit={canEdit} run={run} />}
        {tab === 'boards' && <Boards cat={catalog} canEdit={canEdit} run={run} />}
        {tab === 'edges' && <Edges cat={catalog} canEdit={canEdit} run={run} />}
        {tab === 'suppliers' && <Suppliers cat={catalog} canEdit={canEdit} run={run} />}
        {tab === 'profiles' && <Profiles cat={catalog} canEdit={canEdit} run={run} />}
        {tab === 'import' && <ImportWizard cat={catalog} canEdit={canEdit} onDone={reload} />}
      </main>
    </div>
  );
}

type Run = (fn: () => Promise<unknown>, ok?: string) => Promise<void>;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field" style={{ marginBottom: 6 }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Decors({ cat, canEdit, run }: { cat: Catalog; canEdit: boolean; run: Run }) {
  const [edit, setEdit] = useState<Partial<DecorRow> & { id?: string }>({});
  const [cov, setCov] = useState({ u: '2800', v: '2070' });
  const [formKey, setFormKey] = useState(0); // remount the form (clears file inputs) after save/new
  const save = () =>
    run(async () => {
      const body = { manufacturer: edit.manufacturer ?? '', code: edit.code ?? '', name: edit.name ?? '', finish: edit.finish ?? 'matt', baseColor: edit.baseColor ?? '#cccccc', roughness: edit.roughness ?? 0.7, hasGrain: edit.hasGrain ?? false, maps: edit.maps ?? {} };
      if (edit.id) await api('PUT', `/api/catalog/decors/${edit.id}`, body);
      else await api('POST', '/api/catalog/decors', body);
      setEdit({});
      setFormKey((k) => k + 1);
    }, 'Dekor snimljen.');
  const upload = (kind: 'baseColor' | 'normal' | 'roughness') => async (file: File | undefined) => {
    if (!file) return;
    const u = Math.round(Number(cov.u.replace(',', '.')) * 1000);
    const v = Math.round(Number(cov.v.replace(',', '.')) * 1000);
    if (!(u > 0 && v > 0)) return;
    await run(async () => {
      const { id } = await Api.uploadAsset(file, file.name);
      setEdit((e) => ({ ...e, maps: { ...(e.maps ?? {}), [kind]: { assetId: id, coverageU: u, coverageV: v } } }));
    }, 'Tekstura učitana (snimite dekor).');
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16 }}>
      <table className="grid">
        <thead>
          <tr>
            <th />
            <th>Proizvođač</th>
            <th>Šifra</th>
            <th>Naziv</th>
            <th>Završna obrada</th>
            <th>Teksture</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {cat.decors.map((d) => (
            <tr key={d.id}>
              <td>{d.maps.baseColor ? <img src={`/api/assets/${d.maps.baseColor.assetId}`} width={28} height={28} alt="" style={{ objectFit: 'cover', borderRadius: 3 }} /> : <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: 3, background: d.baseColor }} />}</td>
              <td>{d.manufacturer}</td>
              <td className="mono">{d.code}</td>
              <td>{d.name}</td>
              <td>{d.finish}</td>
              <td className="faint">{Object.keys(d.maps).join(', ') || '—'}</td>
              <td>
                {canEdit && (
                  <>
                    <button className="btn sm ghost" onClick={() => setEdit(d)}>
                      Uredi
                    </button>
                    <button className="btn sm ghost danger" onClick={() => run(() => api('DELETE', `/api/catalog/decors/${d.id}`), 'Arhivirano.')}>
                      ×
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {cat.decors.length === 0 && (
            <tr>
              <td colSpan={7} className="muted">
                Nema dekora. Dodajte ručno ili uvezite cjenovnik ploča.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {canEdit && (
        <div key={`${edit.id ?? 'new'}-${formKey}`} className="card" style={{ padding: 12, boxShadow: 'none', alignSelf: 'start' }}>
          <h3>{edit.id ? 'Uredi dekor' : 'Novi dekor'}</h3>
          <Field label="Proizvođač">
            <input className="input" value={edit.manufacturer ?? ''} onChange={(e) => setEdit({ ...edit, manufacturer: e.target.value })} />
          </Field>
          <Field label="Šifra *">
            <input className="input" value={edit.code ?? ''} onChange={(e) => setEdit({ ...edit, code: e.target.value })} />
          </Field>
          <Field label="Naziv *">
            <input className="input" value={edit.name ?? ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            <Field label="Boja">
              <input className="input" type="color" value={edit.baseColor ?? '#cccccc'} onChange={(e) => setEdit({ ...edit, baseColor: e.target.value })} />
            </Field>
            <Field label="Hrapavost">
              <input className="input" type="number" min={0} max={1} step={0.05} value={edit.roughness ?? 0.7} onChange={(e) => setEdit({ ...edit, roughness: Number(e.target.value) })} />
            </Field>
            <Field label="God">
              <select className="input" value={edit.hasGrain ? '1' : '0'} onChange={(e) => setEdit({ ...edit, hasGrain: e.target.value === '1' })}>
                <option value="0">Bez</option>
                <option value="1">Ima god</option>
              </select>
            </Field>
          </div>
          <h3 style={{ marginTop: 8 }}>Teksture (fizička pokrivenost jednog ponavljanja)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Field label="U (dužina, smjer goda) mm">
              <input className="input" value={cov.u} onChange={(e) => setCov({ ...cov, u: e.target.value })} />
            </Field>
            <Field label="V (širina) mm">
              <input className="input" value={cov.v} onChange={(e) => setCov({ ...cov, v: e.target.value })} />
            </Field>
          </div>
          {(['baseColor', 'normal', 'roughness'] as const).map((k) => (
            <Field key={k} label={`${k === 'baseColor' ? 'Osnovna boja (sRGB)' : k === 'normal' ? 'Normal (linearno, OpenGL +Y)' : 'Roughness (linearno)'}${edit.maps?.[k] ? ' ✓' : ''}`}>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => void upload(k)(e.target.files?.[0])} />
            </Field>
          ))}
          <p className="faint" style={{ fontSize: 11 }}>God teksture mora ići po U osi (horizontalno na slici). Teksture se na serveru ponovo kodiraju, max 4096 px.</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn primary" disabled={!edit.code || !edit.name} onClick={save}>
              Snimi
            </button>
            {edit.id && (
              <button className="btn" onClick={() => setEdit({})}>
                Novi
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Boards({ cat, canEdit, run }: { cat: Catalog; canEdit: boolean; run: Run }) {
  const [f, setF] = useState({ decorId: '', substrate: 'chipboard', thickness: '18', sl: '2800', sw: '2070' });
  const decor = (id: string) => cat.decors.find((d) => d.id === id);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 16 }}>
      <table className="grid">
        <thead>
          <tr>
            <th>Dekor</th>
            <th>Podloga</th>
            <th>Debljina</th>
            <th>Tabla</th>
            <th>Artikli dobavljača</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {cat.boards.map((b) => (
            <tr key={b.id}>
              <td>
                {decor(b.decorId)?.code} {decor(b.decorId)?.name}
              </td>
              <td>{b.substrate}</td>
              <td className="n">{mm(b.thickness)} mm</td>
              <td className="n">
                {mm(b.sheetLength)} × {mm(b.sheetWidth)}
              </td>
              <td className="faint">
                {cat.articles
                  .filter((a) => a.productKind === 'board' && a.productId === b.id)
                  .map((a) => `${cat.suppliers.find((s) => s.id === a.supplierId)?.name ?? '?'} ${a.localCode}: ${fmtPrice(a)}`)
                  .join('; ') || 'nema'}
              </td>
              <td>{canEdit && <button className="btn sm ghost danger" onClick={() => run(() => api('DELETE', `/api/catalog/boards/${b.id}`), 'Arhivirano.')}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div className="card" style={{ padding: 12, boxShadow: 'none', alignSelf: 'start' }}>
          <h3>Nova ploča</h3>
          <Field label="Dekor *">
            <select className="input" value={f.decorId} onChange={(e) => setF({ ...f, decorId: e.target.value })}>
              <option value="">—</option>
              {cat.decors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Podloga">
            <select className="input" value={f.substrate} onChange={(e) => setF({ ...f, substrate: e.target.value })}>
              {['chipboard', 'mdf', 'hdf', 'plywood', 'solid', 'other'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Debljina mm *">
            <input className="input" value={f.thickness} onChange={(e) => setF({ ...f, thickness: e.target.value })} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Field label="Tabla dužina mm">
              <input className="input" value={f.sl} onChange={(e) => setF({ ...f, sl: e.target.value })} />
            </Field>
            <Field label="Tabla širina mm">
              <input className="input" value={f.sw} onChange={(e) => setF({ ...f, sw: e.target.value })} />
            </Field>
          </div>
          <button
            className="btn primary"
            disabled={!f.decorId}
            onClick={() =>
              run(
                () =>
                  api('POST', '/api/catalog/boards', {
                    decorId: f.decorId,
                    substrate: f.substrate,
                    thicknessMm: Number(f.thickness.replace(',', '.')),
                    sheetLengthMm: f.sl ? Number(f.sl) : null,
                    sheetWidthMm: f.sw ? Number(f.sw) : null,
                  }),
                'Ploča dodana.',
              )
            }
          >
            Dodaj
          </button>
        </div>
      )}
    </div>
  );
}

function Edges({ cat, canEdit, run }: { cat: Catalog; canEdit: boolean; run: Run }) {
  const [f, setF] = useState({ manufacturer: '', code: '', name: '', material: 'abs', thickness: '2', width: '22' });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 16 }}>
      <table className="grid">
        <thead>
          <tr>
            <th>Proizvođač</th>
            <th>Šifra</th>
            <th>Naziv</th>
            <th>Materijal</th>
            <th>Debljina × širina</th>
            <th>Cijene</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {cat.edges.map((e) => (
            <tr key={e.id}>
              <td>{e.manufacturer}</td>
              <td className="mono">{e.code}</td>
              <td>{e.name}</td>
              <td>{e.material}</td>
              <td className="n">
                {mm(e.thickness)} × {mm(e.width)}
              </td>
              <td className="faint">
                {cat.articles
                  .filter((a) => a.productKind === 'edge' && a.productId === e.id)
                  .map((a) => `${a.localCode}: ${fmtPrice(a)}`)
                  .join('; ') || 'nema'}
              </td>
              <td>{canEdit && <button className="btn sm ghost danger" onClick={() => run(() => api('DELETE', `/api/catalog/edges/${e.id}`), 'Arhivirano.')}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div className="card" style={{ padding: 12, boxShadow: 'none', alignSelf: 'start' }}>
          <h3>Nova kant traka</h3>
          {(['manufacturer', 'code', 'name'] as const).map((k) => (
            <Field key={k} label={k === 'manufacturer' ? 'Proizvođač' : k === 'code' ? 'Šifra *' : 'Naziv'}>
              <input className="input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
            </Field>
          ))}
          <Field label="Materijal">
            <select className="input" value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })}>
              {['abs', 'pvc', 'pp', 'melamine', 'veneer'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Field label="Debljina mm">
              <input className="input" value={f.thickness} onChange={(e) => setF({ ...f, thickness: e.target.value })} />
            </Field>
            <Field label="Širina mm">
              <input className="input" value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} />
            </Field>
          </div>
          <button
            className="btn primary"
            disabled={!f.code}
            onClick={() =>
              run(() => api('POST', '/api/catalog/edges', { manufacturer: f.manufacturer, code: f.code, name: f.name, material: f.material, thicknessMm: Number(f.thickness.replace(',', '.')), widthMm: Number(f.width.replace(',', '.')) }), 'Traka dodana.')
            }
          >
            Dodaj
          </button>
        </div>
      )}
    </div>
  );
}

function Suppliers({ cat, canEdit, run }: { cat: Catalog; canEdit: boolean; run: Run }) {
  const [name, setName] = useState('');
  const [art, setArt] = useState({ supplierId: '', localCode: '', product: '', price: '', unit: 'm2', tax: '17' });
  const products = [...cat.boards.map((b) => ({ id: `board:${b.id}`, label: `Ploča ${cat.decors.find((d) => d.id === b.decorId)?.code ?? ''} ${b.thickness / 1000} mm` })), ...cat.edges.map((e) => ({ id: `edge:${e.id}`, label: `Traka ${e.code} ${e.thickness / 1000}×${e.width / 1000}` }))];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 16 }}>
      <div>
        {cat.suppliers.map((s) => (
          <div key={s.id} className="card" style={{ padding: 10, boxShadow: 'none', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ marginRight: 'auto' }}>{s.name}</strong>
              {canEdit && (
                <button className="btn sm ghost danger" onClick={() => run(() => api('DELETE', `/api/catalog/suppliers/${s.id}`), 'Arhivirano.')}>
                  Arhiviraj
                </button>
              )}
            </div>
            <table className="grid">
              <tbody>
                {cat.articles
                  .filter((a) => a.supplierId === s.id)
                  .map((a) => (
                    <tr key={a.id}>
                      <td className="mono">{a.localCode}</td>
                      <td>{products.find((p) => p.id === `${a.productKind}:${a.productId}`)?.label ?? '?'}</td>
                      <td className="n" style={{ color: a.priceMinor === null ? 'var(--warn)' : undefined }}>
                        {fmtPrice(a)}
                      </td>
                      <td className="faint">{a.taxRatePermille === null ? 'PDV ?' : `PDV ${a.taxRatePermille / 10}%`}</td>
                      <td className="faint">{a.checkedAt ? new Date(a.checkedAt).toLocaleDateString() : ''}</td>
                      <td>{canEdit && <button className="btn sm ghost danger" onClick={() => run(() => api('DELETE', `/api/catalog/articles/${a.id}`), 'Obrisano.')}>×</button>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
        {cat.suppliers.length === 0 && <p className="muted">Nema dobavljača.</p>}
      </div>
      {canEdit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 12, boxShadow: 'none' }}>
            <h3>Novi dobavljač</h3>
            <Field label="Naziv">
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <button className="btn primary" disabled={!name.trim()} onClick={() => run(() => api('POST', '/api/catalog/suppliers', { name }), 'Dobavljač dodan.').then(() => setName(''))}>
              Dodaj
            </button>
          </div>
          <div className="card" style={{ padding: 12, boxShadow: 'none' }}>
            <h3>Novi artikal (ručno)</h3>
            <Field label="Dobavljač">
              <select className="input" value={art.supplierId} onChange={(e) => setArt({ ...art, supplierId: e.target.value })}>
                <option value="">—</option>
                {cat.suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Šifra dobavljača">
              <input className="input" value={art.localCode} onChange={(e) => setArt({ ...art, localCode: e.target.value })} />
            </Field>
            <Field label="Proizvod">
              <select className="input" value={art.product} onChange={(e) => setArt({ ...art, product: e.target.value, unit: e.target.value.startsWith('edge') ? 'm' : 'm2' })}>
                <option value="">—</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              <Field label="Cijena (prazno = nepoznata)">
                <input className="input" value={art.price} onChange={(e) => setArt({ ...art, price: e.target.value })} />
              </Field>
              <Field label="Jedinica">
                <select className="input" value={art.unit} onChange={(e) => setArt({ ...art, unit: e.target.value })}>
                  <option value="m2">m²</option>
                  <option value="sheet">tabla</option>
                  <option value="m">m</option>
                  <option value="piece">kom</option>
                </select>
              </Field>
              <Field label="PDV %">
                <input className="input" value={art.tax} onChange={(e) => setArt({ ...art, tax: e.target.value })} />
              </Field>
            </div>
            <button
              className="btn primary"
              disabled={!art.supplierId || !art.localCode || !art.product}
              onClick={() => {
                const [kind, productId] = art.product.split(':');
                void run(
                  () =>
                    api('POST', '/api/catalog/articles', {
                      supplierId: art.supplierId,
                      localCode: art.localCode,
                      productKind: kind,
                      productId,
                      price: art.price.trim() ? Number(art.price.replace(',', '.')) : null,
                      unit: art.unit,
                      taxRatePercent: art.tax.trim() ? Number(art.tax.replace(',', '.')) : null,
                    }),
                  'Artikal dodan.',
                );
              }}
            >
              Dodaj artikal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Profiles({ cat, canEdit, run }: { cat: Catalog; canEdit: boolean; run: Run }) {
  const [f, setF] = useState({ name: '', orderSize: 'finished', premill: '0', overhang: '30', waste: '5', step: '0', rounding: 'nearest', maxL: '', maxW: '', segment: false, curved: false });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16 }}>
      <div>
        <p className="muted" style={{ fontSize: 12 }}>
          Profil definiše kako radnja prima mjere (gotova/podloga/rez), dodatke za kant i ograničenja. Profil je „provjeren” tek kada ga ručno potvrdite prema aktuelnom obrascu radnje — demo izvoz nikad ne nosi tuđu oznaku kompatibilnosti.
        </p>
        <table className="grid">
          <thead>
            <tr>
              <th>Naziv</th>
              <th>Mjera za narudžbu</th>
              <th>Predglodanje</th>
              <th>Prepust / otpad</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cat.profiles.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>{p.measurement.orderSize}</td>
                <td className="n">{mm(p.measurement.premillPerBandedEdge)} mm</td>
                <td className="n">
                  {mm(p.measurement.edgeOverhangPerEdge)} mm / {p.measurement.edgeWastePercent}%
                </td>
                <td style={{ color: p.verified ? 'var(--ok)' : 'var(--warn)' }} title={p.verifiedNote}>
                  {p.verified ? 'provjeren' : 'neprovjeren'}
                </td>
                <td>
                  {canEdit && (
                    <button
                      className="btn sm ghost"
                      onClick={() => {
                        const note = p.verified ? '' : (prompt('Kako je provjereno (npr. obrazac radnje, datum)?') ?? '');
                        if (!p.verified && !note.trim()) return;
                        void run(() => api('POST', `/api/catalog/profiles/${p.id}/verify`, { verified: !p.verified, note }), p.verified ? 'Oznaka uklonjena.' : 'Profil označen kao provjeren.');
                      }}
                    >
                      {p.verified ? 'Ukloni provjeru' : 'Označi provjerenim…'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="card" style={{ padding: 12, boxShadow: 'none', alignSelf: 'start' }}>
          <h3>Novi profil radnje</h3>
          <Field label="Naziv">
            <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </Field>
          <Field label="Mjera za narudžbu">
            <select className="input" value={f.orderSize} onChange={(e) => setF({ ...f, orderSize: e.target.value })}>
              <option value="finished">Gotova (radnja oduzima kant)</option>
              <option value="substrate">Sirova podloga</option>
              <option value="cut">Mjera reza (podloga + predglodanje)</option>
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            <Field label="Predglodanje mm">
              <input className="input" value={f.premill} onChange={(e) => setF({ ...f, premill: e.target.value })} />
            </Field>
            <Field label="Prepust kanta mm">
              <input className="input" value={f.overhang} onChange={(e) => setF({ ...f, overhang: e.target.value })} />
            </Field>
            <Field label="Otpad %">
              <input className="input" value={f.waste} onChange={(e) => setF({ ...f, waste: e.target.value })} />
            </Field>
            <Field label="Zaokruž. mm">
              <input className="input" value={f.step} onChange={(e) => setF({ ...f, step: e.target.value })} />
            </Field>
            <Field label="Max dužina">
              <input className="input" value={f.maxL} onChange={(e) => setF({ ...f, maxL: e.target.value })} />
            </Field>
            <Field label="Max širina">
              <input className="input" value={f.maxW} onChange={(e) => setF({ ...f, maxW: e.target.value })} />
            </Field>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={f.segment} onChange={(e) => setF({ ...f, segment: e.target.checked })} /> Radnja podržava segmentno kantiranje
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
            <input type="checkbox" checked={f.curved} onChange={(e) => setF({ ...f, curved: e.target.checked })} /> Podržava krivolinijsko kantiranje
          </label>
          <button
            className="btn primary"
            disabled={!f.name.trim()}
            onClick={() => {
              const n = (s: string) => Number(s.replace(',', '.') || 0);
              void run(
                () =>
                  api('POST', '/api/catalog/profiles', {
                    name: f.name,
                    measurement: { orderSize: f.orderSize, premillPerBandedEdgeMm: n(f.premill), edgeOverhangPerEdgeMm: n(f.overhang), edgeWastePercent: n(f.waste), roundingStepMm: n(f.step), rounding: f.rounding },
                    limits: { ...(f.maxL ? { maxLengthMm: n(f.maxL) } : {}), ...(f.maxW ? { maxWidthMm: n(f.maxW) } : {}), segmentBanding: f.segment, curvedBanding: f.curved },
                  }),
                'Profil dodan (neprovjeren).',
              );
            }}
          >
            Dodaj profil
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- import wizard ----------

const FIELD_LABEL: Record<string, string> = {
  localCode: 'Šifra dobavljača *',
  manufacturer: 'Proizvođač',
  decorCode: 'Šifra dekora *',
  code: 'Šifra trake *',
  name: 'Naziv',
  thickness: 'Debljina (mm) *',
  width: 'Širina (mm) *',
  sheetLength: 'Dužina table (mm)',
  sheetWidth: 'Širina table (mm)',
  substrate: 'Podloga',
  material: 'Materijal trake',
  price: 'Cijena',
  unit: 'Jedinica (m2/tabla)',
  taxRate: 'PDV %',
  currency: 'Valuta',
  availability: 'Dostupnost',
};

const GUESS: Record<string, RegExp> = {
  localCode: /šifra.*dob|^šifra$|^sifra$|art|local/i,
  manufacturer: /proizv|manuf|brand/i,
  decorCode: /dekor|decor/i,
  code: /^kod|^code|šifra.*tr/i,
  name: /naziv|name|opis/i,
  thickness: /deblj|thick/i,
  width: /širin|sirin|width/i,
  sheetLength: /duž.*tab|length/i,
  sheetWidth: /šir.*tab/i,
  substrate: /podlog|substr/i,
  material: /materij/i,
  price: /cijen|cena|price/i,
  unit: /jedin|unit/i,
  taxRate: /pdv|vat|tax/i,
  currency: /valut|curr/i,
};

interface Report {
  dryRun: boolean;
  counts: Record<string, number>;
  report: { row: number; status: string; localCode: string; message: string }[];
}

function ImportWizard({ cat, canEdit, onDone }: { cat: Catalog; canEdit: boolean; onDone(): void }) {
  const [kind, setKind] = useState<'board' | 'edge'>('board');
  const [supplierId, setSupplierId] = useState('');
  const [table, setTable] = useState<{ headers: string[]; rows: string[][]; truncated: boolean; format: string; fields: Record<'board' | 'edge', string[]> } | null>(null);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [onExisting, setOnExisting] = useState<'skip' | 'update-price'>('skip');
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fields = table?.fields[kind] ?? [];

  const guess = (headers: string[], k: 'board' | 'edge', flds: string[]) => {
    const m: Record<string, number> = {};
    for (const f of flds) {
      const i = headers.findIndex((h) => GUESS[f]?.test(h));
      if (i >= 0 && !Object.values(m).includes(i)) m[f] = i;
    }
    void k;
    return m;
  };

  const uploadFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setReport(null);
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await fetch('/api/catalog/import/preview', { method: 'POST', body: fd, credentials: 'same-origin', headers: { 'x-csrf-token': (await Api.me()).csrfToken } });
    const json = await res.json();
    if (!res.ok) return setError(json.message);
    setTable(json);
    setMapping(guess(json.headers, kind, json.fields[kind]));
  };

  const apply = async (dryRun: boolean) => {
    setError(null);
    try {
      const r = await api<Report>('POST', '/api/catalog/import/apply', { kind, supplierId, mapping, rows: table!.rows, onExisting, dryRun });
      setReport(r);
      if (!dryRun) onDone();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const required = kind === 'board' ? ['localCode', 'decorCode', 'name', 'thickness'] : ['localCode', 'code', 'thickness', 'width'];
  const missing = required.filter((f) => mapping[f] === undefined);
  const preview = useMemo(() => table?.rows.slice(0, 8) ?? [], [table]);

  if (!canEdit) return <p className="muted">Uvoz zahtijeva ulogu urednika.</p>;
  return (
    <div className="card" style={{ padding: 16, boxShadow: 'none' }}>
      <h2>Uvoz cjenovnika (CSV / XLSX)</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginTop: 10 }}>
        <Field label="1. Vrsta">
          <select className="input" value={kind} onChange={(e) => { const k = e.target.value as 'board' | 'edge'; setKind(k); if (table) setMapping(guess(table.headers, k, table.fields[k])); setReport(null); }}>
            <option value="board">Ploče (dekor + ploča + artikal)</option>
            <option value="edge">Kant trake</option>
          </select>
        </Field>
        <Field label="2. Dobavljač">
          <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">—</option>
            {cat.suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="3. Fajl">
          <input type="file" accept=".csv,.txt,.xlsx" onChange={(e) => void uploadFile(e.target.files?.[0])} />
        </Field>
        <Field label="Postojeći artikli">
          <select className="input" value={onExisting} onChange={(e) => setOnExisting(e.target.value as 'skip' | 'update-price')}>
            <option value="skip">Preskoči (ne mijenjaj cjenovnik)</option>
            <option value="update-price">Ažuriraj cijenu (prikazuje staru → novu)</option>
          </select>
        </Field>
      </div>
      {cat.suppliers.length === 0 && <p className="error-text">Prvo dodajte dobavljača u kartici „Dobavljači i cijene”.</p>}
      {error && <p className="error-text">{error}</p>}
      {table && (
        <>
          <h3 style={{ marginTop: 12 }}>
            4. Mapiranje kolona ({table.format.toUpperCase()}, {table.rows.length} redova{table.truncated ? ', skraćeno na 5000' : ''})
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
            {fields.map((f) => (
              <Field key={f} label={FIELD_LABEL[f] ?? f}>
                <select
                  className={`input ${required.includes(f) && mapping[f] === undefined ? 'invalid' : ''}`}
                  value={mapping[f] ?? ''}
                  onChange={(e) => {
                    const m = { ...mapping };
                    if (e.target.value === '') delete m[f];
                    else m[f] = Number(e.target.value);
                    setMapping(m);
                  }}
                >
                  <option value="">— ne uvozi —</option>
                  {table.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `Kolona ${i + 1}`}
                    </option>
                  ))}
                </select>
              </Field>
            ))}
          </div>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table className="grid">
              <thead>
                <tr>
                  {table.headers.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    {table.headers.map((_, j) => (
                      <td key={j}>{r[j] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn" disabled={!supplierId || missing.length > 0} onClick={() => void apply(true)} title={missing.length ? `Nedostaje: ${missing.join(', ')}` : undefined}>
              5. Provjeri (bez upisa)
            </button>
            <button className="btn primary" disabled={!supplierId || missing.length > 0 || !report?.dryRun} onClick={() => void apply(false)} title="Prvo pokrenite provjeru">
              6. Uvezi
            </button>
          </div>
        </>
      )}
      {report && (
        <>
          <h3 style={{ marginTop: 14 }}>
            {report.dryRun ? 'Rezultat provjere (ništa nije upisano)' : 'Uvoz završen'}: {Object.entries(report.counts).map(([k, v]) => `${k} ${v}`).join(', ')}
          </h3>
          <table className="grid">
            <tbody>
              {report.report
                .filter((r) => r.status !== 'created' || report.report.length < 50)
                .slice(0, 300)
                .map((r) => (
                  <tr key={r.row}>
                    <td style={{ width: 60 }}>red {r.row}</td>
                    <td className="mono">{r.localCode}</td>
                    <td className={r.status === 'error' || r.status === 'conflict' ? 'case-err' : r.status === 'created' || r.status === 'updated' ? 'case-ok' : 'faint'}>{r.status}</td>
                    <td style={{ whiteSpace: 'normal' }}>{r.message}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
